/**
 * SMBeamDesigner Handlers
 * 
 * All handler functions extracted from the original SMBeamDesigner.tsx
 * These are imported and used by the main coordinator (index.tsx)
 */

import { SMBeamModel, SMBeamNode, SMBeamSpan, SMBeamLoad, CrossSection, UiNotice } from './types'
import { MATERIAL_PRESETS } from './constants'

// Sentinel for the load span selector: 0 means "apply to all spans" (real span ids start at 1).
export const ALL_SPANS_ID = 0

// Rectangular timber fallback entry (b×h in mm); area/inertia computed so there is no hand-arithmetic.
const timberRect = (width: number, height: number, kind: 'solid' | 'glulam'): CrossSection => {
  const widthMeters = width / 1000
  const heightMeters = height / 1000
  return {
    name: kind === 'glulam' ? `${width}×${height} (limtre)` : `${width}×${height}`,
    width,
    height,
    area: widthMeters * heightMeters,
    inertia: (widthMeters * heightMeters ** 3) / 12,
    isCustom: false,
    key: `${kind === 'glulam' ? 'GLULAM' : 'TIMBER'}_${width}x${height}`,
    material: 'timber',
    timberKind: kind,
    sectionType: 'rectangular'
  }
}

type SteelSectionRow = {
  prefix: 'IPE' | 'HEA' | 'HEB'
  size: number
  height: number
  width: number
  webThickness: number
  flangeThickness: number
  kgPerMeter: number
  sectionType: 'I-beam' | 'H-beam'
}

const iSectionInertiaY = (width: number, height: number, webThickness: number, flangeThickness: number): number => {
  const b = width / 1000
  const h = height / 1000
  const tw = webThickness / 1000
  const tf = flangeThickness / 1000
  const hw = h - 2 * tf
  return (b * h ** 3 - (b - tw) * hw ** 3) / 12
}

const iSectionInertiaZ = (width: number, height: number, webThickness: number, flangeThickness: number): number => {
  const b = width / 1000
  const tw = webThickness / 1000
  const tf = flangeThickness / 1000
  const hw = (height - 2 * flangeThickness) / 1000
  return (2 * tf * b ** 3 + hw * tw ** 3) / 12
}

const steelSection = (row: SteelSectionRow): CrossSection => {
  const key = `${row.prefix}${row.size}`
  return {
    name: `${row.prefix} ${row.size}`,
    width: row.width,
    height: row.height,
    area: row.kgPerMeter / MATERIAL_PRESETS.steel.density,
    inertia: iSectionInertiaY(row.width, row.height, row.webThickness, row.flangeThickness),
    inertiaZ: iSectionInertiaZ(row.width, row.height, row.webThickness, row.flangeThickness),
    isCustom: false,
    key,
    material: 'steel',
    sectionType: row.sectionType,
    webThickness: row.webThickness,
    flangeThickness: row.flangeThickness
  }
}

const steelRows: SteelSectionRow[] = [
  { prefix: 'IPE', size: 100, height: 100, width: 55, webThickness: 4.1, flangeThickness: 5.7, kgPerMeter: 8.10, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 120, height: 120, width: 64, webThickness: 4.4, flangeThickness: 6.3, kgPerMeter: 10.4, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 140, height: 140, width: 73, webThickness: 4.7, flangeThickness: 6.9, kgPerMeter: 12.9, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 160, height: 160, width: 82, webThickness: 5.0, flangeThickness: 7.4, kgPerMeter: 15.8, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 180, height: 180, width: 91, webThickness: 5.3, flangeThickness: 8.0, kgPerMeter: 18.8, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 200, height: 200, width: 100, webThickness: 5.6, flangeThickness: 8.5, kgPerMeter: 22.4, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 220, height: 220, width: 110, webThickness: 5.9, flangeThickness: 9.2, kgPerMeter: 26.2, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 240, height: 240, width: 120, webThickness: 6.2, flangeThickness: 9.8, kgPerMeter: 30.7, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 270, height: 270, width: 135, webThickness: 6.6, flangeThickness: 10.2, kgPerMeter: 36.1, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 300, height: 300, width: 150, webThickness: 7.1, flangeThickness: 10.7, kgPerMeter: 42.2, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 330, height: 330, width: 160, webThickness: 7.5, flangeThickness: 11.5, kgPerMeter: 49.1, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 360, height: 360, width: 170, webThickness: 8.0, flangeThickness: 12.7, kgPerMeter: 57.1, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 400, height: 400, width: 180, webThickness: 8.6, flangeThickness: 13.5, kgPerMeter: 66.3, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 450, height: 450, width: 190, webThickness: 9.4, flangeThickness: 14.6, kgPerMeter: 77.6, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 500, height: 500, width: 200, webThickness: 10.2, flangeThickness: 16.0, kgPerMeter: 90.7, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 550, height: 550, width: 210, webThickness: 11.1, flangeThickness: 17.2, kgPerMeter: 105.5, sectionType: 'I-beam' },
  { prefix: 'IPE', size: 600, height: 600, width: 220, webThickness: 12.0, flangeThickness: 19.0, kgPerMeter: 122.4, sectionType: 'I-beam' },

  { prefix: 'HEA', size: 100, height: 96, width: 100, webThickness: 5.0, flangeThickness: 8.0, kgPerMeter: 17.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 120, height: 114, width: 120, webThickness: 5.0, flangeThickness: 8.0, kgPerMeter: 20.3, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 140, height: 133, width: 140, webThickness: 5.5, flangeThickness: 8.5, kgPerMeter: 25.1, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 160, height: 152, width: 160, webThickness: 6.0, flangeThickness: 9.0, kgPerMeter: 31.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 180, height: 171, width: 180, webThickness: 6.0, flangeThickness: 9.5, kgPerMeter: 36.2, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 200, height: 190, width: 200, webThickness: 6.5, flangeThickness: 10.0, kgPerMeter: 43.1, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 220, height: 210, width: 220, webThickness: 7.0, flangeThickness: 11.0, kgPerMeter: 51.5, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 240, height: 230, width: 240, webThickness: 7.5, flangeThickness: 12.0, kgPerMeter: 61.5, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 260, height: 250, width: 260, webThickness: 7.5, flangeThickness: 12.5, kgPerMeter: 69.5, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 280, height: 270, width: 280, webThickness: 8.0, flangeThickness: 13.0, kgPerMeter: 77.8, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 300, height: 290, width: 300, webThickness: 8.5, flangeThickness: 14.0, kgPerMeter: 90.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 320, height: 310, width: 300, webThickness: 9.0, flangeThickness: 15.5, kgPerMeter: 99.5, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 340, height: 330, width: 300, webThickness: 9.5, flangeThickness: 16.5, kgPerMeter: 107.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 360, height: 350, width: 300, webThickness: 10.0, flangeThickness: 17.5, kgPerMeter: 114.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 400, height: 390, width: 300, webThickness: 11.0, flangeThickness: 19.0, kgPerMeter: 127.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 450, height: 440, width: 300, webThickness: 11.5, flangeThickness: 21.0, kgPerMeter: 142.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 500, height: 490, width: 300, webThickness: 12.0, flangeThickness: 23.0, kgPerMeter: 158.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 550, height: 540, width: 300, webThickness: 12.5, flangeThickness: 24.0, kgPerMeter: 169.0, sectionType: 'H-beam' },
  { prefix: 'HEA', size: 600, height: 590, width: 300, webThickness: 13.0, flangeThickness: 25.0, kgPerMeter: 181.0, sectionType: 'H-beam' },

  { prefix: 'HEB', size: 100, height: 100, width: 100, webThickness: 6.0, flangeThickness: 10.0, kgPerMeter: 20.8, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 120, height: 120, width: 120, webThickness: 6.5, flangeThickness: 11.0, kgPerMeter: 27.2, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 140, height: 140, width: 140, webThickness: 7.0, flangeThickness: 12.0, kgPerMeter: 34.4, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 160, height: 160, width: 160, webThickness: 8.0, flangeThickness: 13.0, kgPerMeter: 43.4, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 180, height: 180, width: 180, webThickness: 8.5, flangeThickness: 14.0, kgPerMeter: 52.2, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 200, height: 200, width: 200, webThickness: 9.0, flangeThickness: 15.0, kgPerMeter: 62.5, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 220, height: 220, width: 220, webThickness: 9.5, flangeThickness: 16.0, kgPerMeter: 72.8, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 240, height: 240, width: 240, webThickness: 10.0, flangeThickness: 17.0, kgPerMeter: 84.8, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 260, height: 260, width: 260, webThickness: 10.0, flangeThickness: 17.5, kgPerMeter: 94.8, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 280, height: 280, width: 280, webThickness: 10.5, flangeThickness: 18.0, kgPerMeter: 105.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 300, height: 300, width: 300, webThickness: 11.0, flangeThickness: 19.0, kgPerMeter: 119.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 320, height: 320, width: 300, webThickness: 11.5, flangeThickness: 20.5, kgPerMeter: 129.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 340, height: 340, width: 300, webThickness: 12.0, flangeThickness: 21.5, kgPerMeter: 137.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 360, height: 360, width: 300, webThickness: 12.5, flangeThickness: 22.5, kgPerMeter: 145.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 400, height: 400, width: 300, webThickness: 13.5, flangeThickness: 24.0, kgPerMeter: 158.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 450, height: 450, width: 300, webThickness: 14.0, flangeThickness: 26.0, kgPerMeter: 174.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 500, height: 500, width: 300, webThickness: 14.5, flangeThickness: 28.0, kgPerMeter: 191.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 550, height: 550, width: 300, webThickness: 15.0, flangeThickness: 29.0, kgPerMeter: 203.0, sectionType: 'H-beam' },
  { prefix: 'HEB', size: 600, height: 600, width: 300, webThickness: 15.5, flangeThickness: 30.0, kgPerMeter: 216.0, sectionType: 'H-beam' }
]

const steelSectionCatalog = Object.fromEntries(steelRows.map((row) => [row.prefix + row.size, steelSection(row)]))

// Default section catalog used until templates are loaded from backend
export const defaultSectionCatalog: Record<string, CrossSection> = {
  // Steel profiles
  ...steelSectionCatalog,
  
  // Timber — Konstruksjonsvirke (solid) and Limtre (glulam), tagged so the Tretype filter works
  // offline. The backend serves the full standard catalogue; this is only the fallback subset.
  'TIMBER_48x148': timberRect(48, 148, 'solid'),
  'TIMBER_48x198': timberRect(48, 198, 'solid'),
  'TIMBER_73x198': timberRect(73, 198, 'solid'),
  'TIMBER_98x223': timberRect(98, 223, 'solid'),
  'GLULAM_90x270': timberRect(90, 270, 'glulam'),
  'GLULAM_115x405': timberRect(115, 405, 'glulam'),
  'GLULAM_140x540': timberRect(140, 540, 'glulam'),

  // Concrete rectangular beams (area = b·h, inertia = b·h³/12, SI)
  'CONCRETE_150x300': { name: '150×300', width: 150, height: 300, area: 0.045, inertia: 0.0003375, isCustom: false, key: 'CONCRETE_150x300', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_200x300': { name: '200×300', width: 200, height: 300, area: 0.06, inertia: 0.00045, isCustom: false, key: 'CONCRETE_200x300', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_200x400': { name: '200×400', width: 200, height: 400, area: 0.08, inertia: 0.00106667, isCustom: false, key: 'CONCRETE_200x400', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_200x500': { name: '200×500', width: 200, height: 500, area: 0.10, inertia: 0.00208333, isCustom: false, key: 'CONCRETE_200x500', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_250x400': { name: '250×400', width: 250, height: 400, area: 0.10, inertia: 0.00133333, isCustom: false, key: 'CONCRETE_250x400', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_250x500': { name: '250×500', width: 250, height: 500, area: 0.125, inertia: 0.00260417, isCustom: false, key: 'CONCRETE_250x500', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_250x600': { name: '250×600', width: 250, height: 600, area: 0.15, inertia: 0.0045, isCustom: false, key: 'CONCRETE_250x600', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_300x500': { name: '300×500', width: 300, height: 500, area: 0.15, inertia: 0.003125, isCustom: false, key: 'CONCRETE_300x500', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_300x600': { name: '300×600', width: 300, height: 600, area: 0.18, inertia: 0.0054, isCustom: false, key: 'CONCRETE_300x600', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_300x700': { name: '300×700', width: 300, height: 700, area: 0.21, inertia: 0.008575, isCustom: false, key: 'CONCRETE_300x700', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_350x700': { name: '350×700', width: 350, height: 700, area: 0.245, inertia: 0.01000417, isCustom: false, key: 'CONCRETE_350x700', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_400x800': { name: '400×800', width: 400, height: 800, area: 0.32, inertia: 0.01706667, isCustom: false, key: 'CONCRETE_400x800', material: 'concrete', sectionType: 'rectangular' },
  // Concrete plate strips: 1 m-wide slab design strips (b = 1000 mm), varying thickness
  'CONCRETE_PLATE_1000x150': { name: 'Plate t=150', width: 1000, height: 150, area: 0.15, inertia: 0.00028125, isCustom: false, key: 'CONCRETE_PLATE_1000x150', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_PLATE_1000x180': { name: 'Plate t=180', width: 1000, height: 180, area: 0.18, inertia: 0.000486, isCustom: false, key: 'CONCRETE_PLATE_1000x180', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_PLATE_1000x200': { name: 'Plate t=200', width: 1000, height: 200, area: 0.20, inertia: 0.00066667, isCustom: false, key: 'CONCRETE_PLATE_1000x200', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_PLATE_1000x220': { name: 'Plate t=220', width: 1000, height: 220, area: 0.22, inertia: 0.00088733, isCustom: false, key: 'CONCRETE_PLATE_1000x220', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_PLATE_1000x250': { name: 'Plate t=250', width: 1000, height: 250, area: 0.25, inertia: 0.00130208, isCustom: false, key: 'CONCRETE_PLATE_1000x250', material: 'concrete', sectionType: 'rectangular' },
  'CONCRETE_PLATE_1000x300': { name: 'Plate t=300', width: 1000, height: 300, area: 0.30, inertia: 0.00225, isCustom: false, key: 'CONCRETE_PLATE_1000x300', material: 'concrete', sectionType: 'rectangular' }
}

// Get filtered section presets based on current material
export const getFilteredSectionPresets = (catalog: Record<string, CrossSection>, currentMaterial: string) => {
  return Object.fromEntries(
    Object.entries(catalog).filter(([, section]) => 
      section.material === currentMaterial
    )
  )
}

// ========== LOAD HANDLERS ==========

export const createLoadHandlers = (
  model: SMBeamModel,
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>,
  calculateSelfWeight: (crossSection: CrossSection) => number,
  selectedSpan: number,
  selectedLoadType: string,
  loadForm: any,
  setLoadForm: React.Dispatch<React.SetStateAction<any>>,
  clearResults?: () => void
) => {
  const addLoadToSpan = () => {
    const applyToAll = selectedSpan === ALL_SPANS_ID
    const targetSpanIds = applyToAll
      ? model.spans.map(span => span.id)
      : (model.spans.some(span => span.id === selectedSpan) ? [selectedSpan] : [])
    if (targetSpanIds.length === 0) return

    // A single timestamp keeps the per-span load ids unique (id includes the span id) yet grouped.
    const stamp = Date.now()
    const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max)

    // Builds the load for one span, using that span's own length/id (positions clamped into the
    // span so applying to spans of different lengths never produces an out-of-span load). Returns
    // null to skip a span (e.g. self-weight already present).
    const buildLoadForSpan = (span: SMBeamSpan): SMBeamLoad | null => {
      const spanLength = span.length
      if (selectedLoadType === 'SelfWeight') {
        if (span.loads.some(l => l.id === `selfweight-span-${span.id}`)) return null
        const sw = calculateSelfWeight(model.crossSection)
        return {
          id: `selfweight-span-${span.id}`,
          type: 'UniformLoad',
          spanId: span.id,
          category: 'Permanent',
          pointLoad: 0,
          pointLoadPosition: 0,
          normalForce: 0,
          normalForcePosition: 0,
          normalForceEccentricity: 0,
          uniformIntensity: -sw,
          startIntensity: -sw,
          endIntensity: -sw,
          startPosition: 0,
          endPosition: spanLength
        }
      }
      const endPos = clamp(loadForm.endPosition || spanLength, spanLength)
      return {
        id: `${span.id}-${selectedLoadType}-${stamp}`,
        type: selectedLoadType as SMBeamLoad['type'],
        spanId: span.id,
        category: loadForm.category || 'Variable',
        pointLoad: selectedLoadType === 'PointLoad' ? loadForm.pointLoad : 0,
        pointLoadPosition: selectedLoadType === 'PointLoad' ? clamp(loadForm.pointLoadPosition, spanLength) : 0,
        normalForce: selectedLoadType === 'AxialForce' ? loadForm.axialForceValue : loadForm.normalForce,
        // Apply axial force exactly at beam boundaries or at user-specified position.
        // For end axial forces, use x = 0 or x = L so solver and diagrams line up with nodes.
        normalForcePosition: selectedLoadType === 'AxialForce'
          ? (loadForm.axialForceEnd === 'left' ? 0 : spanLength)
          : clamp(loadForm.normalForcePosition, spanLength),
        normalForceEccentricity: loadForm.normalForceEccentricity,
        uniformIntensity: selectedLoadType === 'UniformLoad' ? loadForm.uniformIntensity : 0,
        startIntensity: selectedLoadType === 'TriangularRight' || selectedLoadType === 'TriangularLeft' ? loadForm.startIntensity : 0,
        endIntensity: selectedLoadType === 'TriangularRight' || selectedLoadType === 'TriangularLeft' ? loadForm.endIntensity : 0,
        startPosition: selectedLoadType === 'UniformLoad' ? 0 : clamp(loadForm.startPosition, spanLength),
        endPosition: selectedLoadType === 'UniformLoad' ? spanLength : endPos,
        axialForceEnd: selectedLoadType === 'AxialForce' ? loadForm.axialForceEnd : undefined
      }
    }

    setModel(prev => ({
      ...prev,
      spans: prev.spans.map(span => {
        if (!targetSpanIds.includes(span.id)) return span
        const newLoad = buildLoadForSpan(span)
        return newLoad ? { ...span, loads: [...span.loads, newLoad] } : span
      })
    }))

    if (clearResults) {
      clearResults()
    }

    // Reset form
    const resetSpanLength = model.spans.find(span => targetSpanIds.includes(span.id))?.length ?? 5
    const newPosition = resetSpanLength / 2
    setLoadForm({
      pointLoad: -10,
      pointLoadPosition: newPosition,
      normalForce: 0,
      normalForcePosition: newPosition,
      normalForceEccentricity: 0,
      uniformIntensity: -10,
      startIntensity: -20,
      endIntensity: -10,
      startPosition: 0,
      endPosition: resetSpanLength,
      axialForceEnd: 'right',
      axialForceValue: 50,
      category: loadForm.category || 'Variable'
    })
  }

  const removeLoad = (loadId: string, spanId: number) => {
    setModel(prev => ({
      ...prev,
      spans: prev.spans.map(span =>
        span.id === spanId
          ? { ...span, loads: span.loads.filter(load => load.id !== loadId) }
          : span
      )
    }))

    if (clearResults) {
      clearResults()
    }
  }

  const editLoad = (loadId: string, spanId: number) => {
    const spanObj = model.spans.find(span => span.id === spanId)
    if (!spanObj) return

    const spanLength = spanObj.length
    const endPos = loadForm.endPosition || spanLength

    const updatedLoad: SMBeamLoad = {
      id: loadId,
      type: selectedLoadType as SMBeamLoad['type'],
      spanId,
      category: loadForm.category || 'Variable',
      pointLoad: selectedLoadType === 'PointLoad' ? loadForm.pointLoad : 0,
      pointLoadPosition: selectedLoadType === 'PointLoad' ? loadForm.pointLoadPosition : 0,
      normalForce: selectedLoadType === 'AxialForce' ? loadForm.axialForceValue : loadForm.normalForce,
      normalForcePosition: selectedLoadType === 'AxialForce'
        ? (loadForm.axialForceEnd === 'left' ? 0 : spanLength)
        : loadForm.normalForcePosition,
      normalForceEccentricity: loadForm.normalForceEccentricity,
      uniformIntensity: selectedLoadType === 'UniformLoad' ? loadForm.uniformIntensity : 0,
      startIntensity: selectedLoadType === 'TriangularRight' || selectedLoadType === 'TriangularLeft' ? loadForm.startIntensity : 0,
      endIntensity: selectedLoadType === 'TriangularRight' || selectedLoadType === 'TriangularLeft' ? loadForm.endIntensity : 0,
      startPosition: selectedLoadType === 'UniformLoad' ? 0 : loadForm.startPosition,
      endPosition: selectedLoadType === 'UniformLoad' ? spanLength : endPos,
      axialForceEnd: selectedLoadType === 'AxialForce' ? loadForm.axialForceEnd : undefined
    }

    setModel(prev => ({
      ...prev,
      spans: prev.spans.map(span =>
        span.id === spanId
          ? { ...span, loads: span.loads.map(load => load.id === loadId ? updatedLoad : load) }
          : span
      )
    }))

    if (clearResults) {
      clearResults()
    }

    // Reset form
    const newPosition = spanLength / 2
    setLoadForm({
      pointLoad: -10,
      pointLoadPosition: newPosition,
      normalForce: 0,
      normalForcePosition: newPosition,
      normalForceEccentricity: 0,
      uniformIntensity: -10,
      startIntensity: -20,
      endIntensity: -10,
      startPosition: 0,
      endPosition: spanLength,
      axialForceEnd: 'right',
      axialForceValue: 50,
      category: loadForm.category || 'Variable'
    })
  }

  return {
    addLoadToSpan,
    removeLoad,
    editLoad
  }
}

// Create handlers factory
export const createHandlers = (
  model: SMBeamModel,
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>,
  currentMaterial: string,
  calculateSelfWeight: (crossSection: CrossSection) => number,
  setSelectedSpan: React.Dispatch<React.SetStateAction<number>>,
  setSupportNotice?: (notice: UiNotice | null) => void,
  clearResults?: () => void
) => {
  
  const addSpan = () => {
    // Clear previous analysis results when geometry changes
    if (clearResults) {
      console.log('🔴 CLEARING RESULTS - addSpan called')
      clearResults()
    } else {
      console.warn('⚠️ clearResults not available in addSpan')
    }
    
    const lastNode = model.nodes[model.nodes.length - 1]
    const maxNodeId = Math.max(...model.nodes.map(n => n.id))
    const maxSpanId = Math.max(...model.spans.map(s => s.id))
    const newNodeId = maxNodeId + 1
    const newSpanId = maxSpanId + 1
    const spanLength = 4.0

    const selfWeightValue = calculateSelfWeight(model.crossSection)

    const newNode: SMBeamNode = {
      id: newNodeId,
      supportType: 'Roller',
      axialFixed: false,
      position: lastNode.position + spanLength
    }

    const newSpan: SMBeamSpan = {
      id: newSpanId,
      leftNodeId: lastNode.id,
      rightNodeId: newNodeId,
      length: spanLength,
      E: model.spans[0].E,
      A: model.crossSection.area,
      I: model.crossSection.inertia,
      loads: [
        {
          id: `selfweight-span-${newSpanId}`,
          type: 'UniformLoad',
          spanId: newSpanId,
          category: 'Permanent',
          pointLoad: 0,
          pointLoadPosition: 0,
          normalForce: 0,
          normalForcePosition: 0,
          normalForceEccentricity: 0,
          uniformIntensity: -selfWeightValue,
          startIntensity: -selfWeightValue,
          endIntensity: -selfWeightValue,
          startPosition: 0,
          endPosition: spanLength
        }
      ]
    }

    setModel(prev => {
      const updatedNodes: SMBeamNode[] = [...prev.nodes, newNode]
      const updatedSpans: SMBeamSpan[] = [...prev.spans, newSpan]

      let updatedRebarZones = prev.rebarZones

      // For concrete beams: ensure new span is not without reinforcement.
      // Extend the last rebar zone to cover the new total beam length, so
      // minimum reinforcement (already provided in existing zones) also
      // applies on the new span.
      if (currentMaterial === 'concrete' && prev.rebarZones && prev.rebarZones.length > 0) {
        const zones = [...prev.rebarZones]
        const lastIndex = zones.length - 1
        const lastZone = zones[lastIndex]

        const rightmostNode = updatedNodes[updatedNodes.length - 1]
        const newBeamEndPosition = rightmostNode.position

        if (lastZone.end < newBeamEndPosition) {
          zones[lastIndex] = {
            ...lastZone,
            end: newBeamEndPosition
          }
        }

        updatedRebarZones = zones
      }

      return {
        ...prev,
        nodes: updatedNodes,
        spans: updatedSpans,
        rebarZones: updatedRebarZones
      }
    })
  }

  const removeSpan = (spanId: number) => {
    if (model.spans.length <= 1) {
      alert('Cannot remove the last span. A beam must have at least one span.')
      return
    }

    // Clear previous analysis results when geometry changes
    if (clearResults) {
      clearResults()
    }

    setModel(prev => {
      const remainingSpans = prev.spans.filter(span => span.id !== spanId)
      const sortedSpans = [...remainingSpans].sort((a, b) => {
        const aLeftNode = prev.nodes.find(n => n.id === a.leftNodeId)
        const bLeftNode = prev.nodes.find(n => n.id === b.leftNodeId)
        return (aLeftNode?.position || 0) - (bLeftNode?.position || 0)
      })

      const newNodes: SMBeamNode[] = []
      const oldNodeIdToNewId = new Map<number, number>()
      let currentPosition = 0
      
      sortedSpans.forEach((span, index) => {
        const oldLeftNode = prev.nodes.find(n => n.id === span.leftNodeId)
        const newLeftNodeId = index + 1
        if (!oldNodeIdToNewId.has(span.leftNodeId)) {
          newNodes.push({
            id: newLeftNodeId,
            supportType: oldLeftNode?.supportType || 'Free',
            axialFixed: oldLeftNode?.axialFixed || false,
            position: currentPosition
          })
          oldNodeIdToNewId.set(span.leftNodeId, newLeftNodeId)
        }
        
        if (index === sortedSpans.length - 1) {
          const oldRightNode = prev.nodes.find(n => n.id === span.rightNodeId)
          const newRightNodeId = newLeftNodeId + 1
          currentPosition += span.length
          newNodes.push({
            id: newRightNodeId,
            supportType: oldRightNode?.supportType || 'Roller',
            axialFixed: oldRightNode?.axialFixed || false,
            position: currentPosition
          })
          oldNodeIdToNewId.set(span.rightNodeId, newRightNodeId)
        } else {
          currentPosition += span.length
        }
      })

      const newSpans: SMBeamSpan[] = sortedSpans.map((span, index) => {
        const newSpanId = index + 1
        const newLeftNodeId = oldNodeIdToNewId.get(span.leftNodeId) || (index + 1)
        const newRightNodeId = oldNodeIdToNewId.get(span.rightNodeId) || (index + 2)
        
        const updatedLoads = span.loads.map(load => {
          let newLoadId = load.id
          if (load.id.includes('selfweight-span-')) {
            newLoadId = `selfweight-span-${newSpanId}`
          } else {
            newLoadId = load.id.replace(`${span.id}-`, `${newSpanId}-`)
          }
          
          return {
            ...load,
            id: newLoadId,
            spanId: newSpanId
          }
        })

        return {
          ...span,
          id: newSpanId,
          leftNodeId: newLeftNodeId,
          rightNodeId: newRightNodeId,
          loads: updatedLoads
        }
      })

      return {
        ...prev,
        nodes: newNodes,
        spans: newSpans
      }
    })

    setSelectedSpan(1)
  }

  const updateNodeSupport = (nodeId: number, supportType: SMBeamNode['supportType']) => {
    const isInternalNode = nodeId !== model.nodes[0].id && nodeId !== model.nodes[model.nodes.length - 1].id
    
    if (isInternalNode && supportType === 'Fixed') {
      if (setSupportNotice) {
        setSupportNotice({
          id: 'internal-fixed-support',
          title: 'Fixed support cannot be applied at internal nodes.',
          description: 'A fixed support at an internal node would prevent moment and rotation transfer between spans, effectively splitting the continuous beam into separate segments.',
          bullets: [
            'Pinned – allows rotation transfer (continuous beam behavior)',
            'Roller – allows rotation and horizontal movement'
          ],
          variant: 'info'
        })
      }
      return
    }
    
    setModel(prev => ({
      ...prev,
      nodes: prev.nodes.map(node => 
        node.id === nodeId ? { ...node, supportType } : node
      )
    }))
  }

  const updateSpanLength = (spanId: number, newLength: number) => {
    // Clear previous analysis results when geometry changes
    if (clearResults) {
      clearResults()
    }

    const span = model.spans.find(s => s.id === spanId)
    if (!span || newLength === span.length) return

    const invalidLoads: SMBeamLoad[] = []

    span.loads.forEach(load => {
      if (load.id.includes('selfweight')) {
        return
      }

      if (load.type === 'PointLoad' && load.pointLoadPosition > newLength) {
        invalidLoads.push(load)
      } else if ((load.type === 'TriangularRight' || load.type === 'TriangularLeft') &&
                 (load.startPosition > newLength || load.endPosition > newLength)) {
        invalidLoads.push(load)
      } else if (load.type === 'UniformLoad') {
        const isFullSpan = load.startPosition === 0 && load.endPosition === span.length
        if (!isFullSpan && (load.startPosition > newLength || load.endPosition > newLength)) {
          invalidLoads.push(load)
        }
      }
    })

    if (invalidLoads.length > 0) {
      let message = `Span length changed from ${span.length}m to ${newLength}m:\n\n`
      message += `⚠️ ${invalidLoads.length} load(s) will be REMOVED because they are outside the new span length:\n\n`
      
      invalidLoads.forEach(load => {
        if (load.type === 'PointLoad') {
          message += `  • Point load: ${load.pointLoad} kN at ${load.pointLoadPosition}m\n`
        } else {
          message += `  • ${load.type}: ${load.startPosition}m to ${load.endPosition}m\n`
        }
      })
      
      message += `\nContinue?`

      if (!confirm(message)) {
        return
      }
    }

    setModel(prev => ({
      ...prev,
      spans: prev.spans.map(s => {
        if (s.id !== spanId) return s

        const validLoads = s.loads.filter(load => !invalidLoads.includes(load))

        const adjustedLoads = validLoads.map(load => {
          if (load.id.includes('selfweight')) {
            return {
              ...load,
              endPosition: newLength
            }
          }
          
          if (load.type === 'UniformLoad' && load.startPosition === 0 && load.endPosition === s.length) {
            return {
              ...load,
              endPosition: newLength
            }
          }
          
          return load
        })

        return {
          ...s,
          length: newLength,
          loads: adjustedLoads
        }
      })
    }))

    setModel(prev => {
      let currentPosition = 0
      const updatedNodes = prev.nodes.map(node => {
        const nodeSpan = prev.spans.find(span => span.leftNodeId === node.id)
        if (nodeSpan) {
          const updatedNode = { ...node, position: currentPosition }
          currentPosition += nodeSpan.id === spanId ? newLength : nodeSpan.length
          return updatedNode
        } else {
          return { ...node, position: currentPosition }
        }
      })

      return {
        ...prev,
        nodes: updatedNodes
      }
    })
  }

  return {
    addSpan,
    removeSpan,
    updateNodeSupport,
    updateSpanLength
  }
}

